import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import { AuthLinkResultCard } from "@/components/Auth"
import { ErrorCode, getLimitErrorTranslationKey } from "@/constants/errors"
import { useSignupVerifyApi } from "@/hooks/api/account"
import type { ApiErrorWithData } from "@/lib/authErrors"

const signupVerificationRequests = new Map<string, Promise<void>>()

const SignupVerify = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { fetchFn: verifySignup } = useSignupVerifyApi()
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  )
  const [message, setMessage] = useState(t("common.auth.verify.verifying"))
  const [canRetry, setCanRetry] = useState(false)

  const verifyLink = useCallback(
    (onCancelled: () => boolean) => {
      const uid = searchParams.get("uid")
      const token = searchParams.get("token")
      const invitationToken = searchParams.get("invitation_token")
      if (!uid || !token) {
        setStatus("error")
        setCanRetry(false)
        setMessage(t("common.auth.verify.errors.missingInfo"))
        return
      }

      setStatus("loading")
      setCanRetry(false)
      setMessage(t("common.auth.verify.verifying"))

      const requestKey = `${uid}:${token}:${invitationToken || ""}`
      let request = signupVerificationRequests.get(requestKey)
      if (!request) {
        request = verifySignup({
          jsonBody: {
            uid,
            token,
            invitation_token: invitationToken || undefined,
          },
        })
          .then((resp) => {
            if (!resp?.data) {
              throw new Error(t("common.auth.verify.errors.missingUser"))
            }
            localStorage.setItem("loggedIn", JSON.stringify({ type: "email" }))
          })
          .finally(() => {
            signupVerificationRequests.delete(requestKey)
          })
        signupVerificationRequests.set(requestKey, request)
      }

      void request
        .then(() => {
          if (onCancelled()) return
          setStatus("success")
          setCanRetry(false)
          setMessage(t("common.auth.verify.success"))
        })
        .catch((error) => {
          if (onCancelled()) return
          const apiError = error as ApiErrorWithData
          const errorCode = apiError.data?.error_code
          setStatus("error")
          if (
            apiError.response?.status === 400 &&
            (!errorCode || errorCode === ErrorCode.SYS_BAD_REQUEST)
          ) {
            setCanRetry(false)
            setMessage(t("common.auth.verify.errors.invalidOrExpired"))
            return
          }
          const limitErrorKey = getLimitErrorTranslationKey(errorCode)
          if (limitErrorKey) {
            setCanRetry(false)
            setMessage(t(limitErrorKey))
            return
          }
          if (
            errorCode === ErrorCode.INVITATION_INVALID ||
            errorCode === ErrorCode.INVITATION_EXPIRED ||
            errorCode === ErrorCode.INVITATION_NO_LONGER_VALID ||
            errorCode === ErrorCode.INVITATION_EMAIL_MISMATCH
          ) {
            setCanRetry(false)
            setMessage(t(`common.auth.login.errorCodes.${errorCode}`))
            return
          }
          setCanRetry(true)
          setMessage(
            apiError.response?.status === 429
              ? t("common.auth.errors.tooManyRequests")
              : t("common.auth.verify.errors.validationFailed")
          )
        })
    },
    [searchParams, verifySignup, t]
  )

  useEffect(() => {
    let cancelled = false
    verifyLink(() => cancelled)

    return () => {
      cancelled = true
    }
  }, [verifyLink])

  return (
    <div className="container flex min-h-[600px] items-center justify-center">
      <AuthLinkResultCard
        title={t("common.auth.verify.title")}
        description={message}
        variant={
          status === "loading"
            ? "loading"
            : status === "success"
              ? "success"
              : "error"
        }
        primaryAction={
          status === "loading"
            ? undefined
            : {
                label:
                  status === "success"
                    ? t("common.auth.actions.continue")
                    : canRetry
                      ? t("common.auth.actions.retry")
                      : t("common.auth.actions.backToLogin"),
                onClick: () => {
                  if (status === "success") {
                    navigate("/")
                  } else if (canRetry) {
                    verifyLink(() => false)
                  } else {
                    navigate("/login")
                  }
                },
              }
        }
      />
    </div>
  )
}

export default SignupVerify
